import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { colord } from "colord";

import { misc } from "app/themes/colors";
import { QBSyncIcon } from "components/icons";
import QuickBooksProductSelect from "features/quickbooks/quickbooks-products/components/columns/quickbooks-product-edit/quickbooks-product-select.component";
import {
  type QuickBookCompany,
  type QuickBookProduct
} from "services/types/quickbooks.type";

import SelectQuickbooksCompany from "./select-quickbooks-company.component";

type QuickbooksProductMappingSelectProps = {
  sx?: SxProps;
  quickBooksCompany: QuickBookCompany | null;
  quickBooksProduct: Partial<QuickBookProduct> | null;
  onChange: (
    quickBooksCompany: QuickBookCompany | null,
    quickBooksProduct: Partial<QuickBookProduct> | null
  ) => void;
};

export default function QuickbooksProductMappingSelect({
  quickBooksCompany,
  quickBooksProduct,
  onChange,
  sx
}: QuickbooksProductMappingSelectProps) {
  const [company, setCompany] = useState<QuickBookCompany | null>(null);

  const [product, setProduct] = useState<Partial<QuickBookProduct> | null>(
    null
  );

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setCompany(quickBooksCompany || null);
    if (initialized) {
      setProduct(quickBooksCompany ? quickBooksProduct || null : null);
    }
  }, [initialized, quickBooksCompany, quickBooksProduct]);

  return (
    <Stack
      mb={4}
      p={3}
      pt={2}
      pb={4}
      borderRadius="8px"
      sx={{
        background: colord(misc.quickbooks).alpha(0.05).toHex(),
        border: `solid 2px ${colord(misc.quickbooks).alpha(0.075).toHex()}`,
        boxSizing: "border-box"
      }}
    >
      <Stack direction="row" columnGap={1} alignItems="center" mb={1.5}>
        <QBSyncIcon />

        <Typography sx={{ fontSize: "24px", lineHeight: 1.5 }}>
          Intuit QuickBooks
        </Typography>
      </Stack>

      <Typography variant="body2" mb={2.5}>
        Map this product to your QuickBooks product
      </Typography>

      <Stack direction="row">
        <Box width="calc(25% - 24px)">
          <SelectQuickbooksCompany
            label="QuickBooks Company"
            value={company}
            initialized={initialized}
            setInitialized={setInitialized}
            disablePortal
            onOptionChanged={(value: QuickBookCompany | null) => {
              setCompany(value);
              if (!value) {
                setProduct(null);
                onChange(value, null);
              } else {
                onChange(value, initialized ? product : quickBooksProduct);
              }
            }}
          />
        </Box>
        <Box width="calc(50% - 16px)" ml={2}>
          {company && (
            <QuickBooksProductSelect
              label="QuickBooks Product"
              placeholder="Search by Name"
              disabled={!company}
              quickBooksCompanyId={Number(company?.id || 0)}
              value={product || null}
              disablePortal
              onOptionChanged={(value: Partial<QuickBookProduct> | null) => {
                setProduct(value);
                onChange(company, value);
              }}
              sx={{ pl: 2 }}
            />
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
